/**
 * Thrown by every engine entry point in the Phase 0 scaffold.
 * The public API surface is fully typed; implementations land
 * incrementally behind these signatures during Phase 0.
 */
export class NotImplementedError extends Error {
    constructor(feature) {
        super(`@sae/engine: ${feature} is not implemented yet (Phase 0 scaffold)`);
        this.name = 'NotImplementedError';
    }
}
/**
 * Thrown when a Fay-Herriot fit is refused because too few in-sample
 * areas are available (decision S4: warn below ten areas, refuse below
 * five; the refusal is the fit-level floor, brief 9.15 / MET-14). The
 * QC issue is attached so callers can surface it through the same
 * catalog as every other engine flag.
 */
/**
 * Thrown when a published (area-level) direct estimate lies outside
 * [0, 1] on the proportion scale — a natural-scale input (a mean, a
 * total, a rate per 1,000) fed to a proportion-only tool. The refusal
 * happens before any transformation or estimation: before the
 * Tier 3 verdict fixes round (2026-11-01) the area-level transform
 * silently clamped such inputs into [0, 1] and the path published
 * estimates of 1.000 with intervals [0, 1] for them (benchmark
 * confirmation record, CONF_MANIFEST section 4; re-probed on this
 * tree at the fixes round). The verdict of 2026-11-01 (Section 3)
 * requires the clean refusal with a plain message, covered by a
 * registered test. The QC issue is attached so callers can surface
 * it through the same catalog as every other engine flag.
 */
export class NonProportionEstimateError extends Error {
    issue;
    /** The offending inputs, in input order: area and its estimate. */
    offenders;
    constructor(offenders) {
        const shown = offenders
            .slice(0, 5)
            .map((o) => `${o.areaId}: ${o.value}`)
            .join('; ');
        super('SAE Studio estimates proportions between 0 and 1; your outcome ' +
            `has values outside that range (${shown}` +
            `${offenders.length > 5 ? `; and ${offenders.length - 5} more` : ''}). ` +
            'Nothing was estimated.');
        this.name = 'NonProportionEstimateError';
        this.offenders = offenders;
        this.issue = {
            code: 'ESTIMATE_OUT_OF_RANGE',
            severity: 'blocking',
            message: this.message,
        };
    }
}
export class InsufficientAreasError extends Error {
    issue;
    areaCount;
    minimumAreas;
    constructor(areaCount, minimumAreas) {
        super(`fitFayHerriot: at least ${minimumAreas} in-sample areas are required ` +
            `to fit a Fay-Herriot model (brief 9.15, decision S4); received ${areaCount}.`);
        this.name = 'InsufficientAreasError';
        this.areaCount = areaCount;
        this.minimumAreas = minimumAreas;
        this.issue = { code: 'MET-14', severity: 'blocking', message: this.message };
    }
}
//# sourceMappingURL=errors.js.map